import { errorForm, navigation } from '@origin-digital/reporting-client';
import { AxiosPromise } from 'axios';
import * as React from 'react';
import { ChangeEvent, FocusEvent, FormEvent, useEffect } from 'react';
import { AccountResponse } from 'ts/services/find-account/AccountResponse';
import { AccountNumberView } from './AccountNumberView';
import { Content } from './Interfaces';

interface AccountNumberPageProps {
  history: any;
  nextAction: (email: string) => void;
  errorPageAction: (statusCode: number) => void;
  findAccountAction: (
    lastname: string,
    dob: string,
    postcode: string,
    accountNumber: string,
    userProvidedEmail?: string,
  ) => AxiosPromise<AccountResponse>;
  lastName: string;
  dob: string;
  postcode: string;
  userProvidedEmail?: string;
  content: Content;
}

interface AccountNumberPageState {
  accountNumber: string;
  accountNumberError: string;
  spinner: boolean;
  alertBoxError: string;
}

const AccountNumberPage: React.FC<AccountNumberPageProps> = ({
  history,
  nextAction,
  errorPageAction,
  findAccountAction,
  lastName,
  dob,
  postcode,
  userProvidedEmail,
  content,
}) => {
  const [state, setState] = React.useState<AccountNumberPageState>({
    accountNumber: '',
    accountNumberError: '',
    spinner: false,
    alertBoxError: '',
  });

  useEffect(() => {
    navigation('screen', {
      currentUri: '',
      friendlyUri: content.analytics.navigation.friendlyUri,
    });
  }, []);

  const accountNumberTransform = (value: string, prevValue: string) => {
    // only numbers no spaces
    const sapOrKrakenAccountId = accountRegex(value);
    // limit characters to 12
    return sapOrKrakenAccountId.substring(0, 12);
  };

  const accountRegex = (value: string) => {
    return value.replace(/[^0-9a-zA-Z\-]+/g, '');
  };

  const onAccountNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    checkValidAccountNumber();
  };

  const checkValidAccountNumber = (): boolean => {
    const isValidAccountNumber =
      state.accountNumber.length === 12 || state.accountNumber.length === 10;
    if (!isValidAccountNumber) {
      setState({
        ...state,
        accountNumberError: 'Your account number should be 12 digits or 10 characters.',
      });
    }
    return isValidAccountNumber;
  };

  const onAccountNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, accountNumber: e.target.value, accountNumberError: '' });
  };

  const onSubmit = async (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!checkValidAccountNumber()) {
      return;
    }

    setState({ ...state, spinner: true });
    try {
      const accountsResponse = await findAccountAction(
        lastName,
        dob,
        postcode,
        state.accountNumber,
        userProvidedEmail,
      );
      nextAction(accountsResponse.data.email);
    } catch (error) {
      setState({ ...state, spinner: false });
      const { response = {} } = error || {};
      response.status === 404 ? handleNoAccount() : errorPageAction(response.status);
    }
  };

  const handleNoAccount = () => {
    const { formName, errorMessage, errorCode } = content.analytics.errorForm;
    errorForm({
      formName,
      errorMessage,
      errorCode,
    });
    setState({ ...state, alertBoxError: content.copy.alertBoxText });
  };
  return (
    <AccountNumberView
      content={content}
      onSubmit={onSubmit}
      onAccountNumberChange={onAccountNumberChange}
      onAccountNumberBlur={onAccountNumberBlur}
      accountNumberError={state.accountNumberError}
      history={history}
      spinner={state.spinner}
      alertBoxError={state.alertBoxError}
      accountNumberTransform={accountNumberTransform}
    />
  );
};

export { AccountNumberPage };
